"use client";

import Divider from "@/components/ui/Divider";
import { cn } from "@/lib/utils";
import React, { forwardRef, useRef } from "react";

interface SectionProps {
  id?: string;
  title?: string;
  description?: string;
  descriptionSlot?: React.ReactNode; // Replaces Vue's description slot
  children?: React.ReactNode; // Replaces Vue's content slot
  className?: string; // Optional for additional styling
  centerTitle?: boolean; // Center title and description
  stackDescription?: boolean; // Stack description beneath title on all screens
  topDivider?: boolean; // add divider to top
  bottomDivider?: boolean; // add divider to bottom
  fullScreen?: boolean; // Enable full-screen-section class (default: true)
}

const PageSection = forwardRef<HTMLElement, SectionProps>(
  (
    {
      id,
      title,
      description,
      descriptionSlot,
      children,
      className,
      centerTitle = true,
      stackDescription = true,
      topDivider = false,
      bottomDivider = false,
      fullScreen = true,
    },
    ref
  ) => {
    const sectionRef = useRef<HTMLElement | null>(null);

    // Handle ref assignment
    const setRef = (node: HTMLElement | null) => {
      sectionRef.current = node; // Safe: sectionRef is MutableRefObject
      if (typeof ref === "function") {
        // Handle callback ref
        ref(node);
      } else if (ref && "current" in ref) {
        // Handle mutable ref object (avoid read-only RefObject)
        // Type assertion to allow assignment
        (ref as React.MutableRefObject<HTMLElement | null>).current = node;
      }
    };

    // Pass sectionRef to children via cloneElement, but only for custom components
    const childrenWithRef = React.Children.map(children, (child) => {
      if (
        React.isValidElement(child) &&
        typeof child.type !== "string" // Only add to custom components
      ) {
        return React.cloneElement(child as React.ReactElement, {
          parentContainerRef: sectionRef,
        });
      }
      return child;
    });

    return (
      <section
        id={id}
        ref={setRef}
        className={cn(
          "text-muted-foreground font-sans",
          fullScreen && "full-screen-section",
          // TODO: review these margins...
          // 'mt-12',
          // 'mb-5',
          "scroll-mt-[115px]",
          // topDivider ? 'border-t-4 border-primary':'',
          // bottomDivider ? 'border-b-4 border-primary':'',
          className
        )}
      >
        <div className="container px-5 mx-auto">
          <div className="flex flex-col">
            {/*{topDivider && (<Divider/>)}*/}

            {/* Title and Description */}
            <div
              className={cn(
                "flex flex-col py-6 mb-2",
                stackDescription ? "flex-col" : "sm:flex-row flex-wrap", // Stack on all screens if stackDescription is true
                centerTitle && "items-center text-center" // Center title and description if centerTitle is true
              )}
            >
              {title && (
                <h2
                  className={cn(
                    "text-foreground font-extrabold text-5xl mb-0 sm:mb-0 dark:text-white font-don",
                    stackDescription ? "w-full" : "sm:w-2/5", // Full width if stacked
                    centerTitle && "mx-auto" // Center title
                    // 'underline decoration-red-600',
                  )}
                >
                  <span className="underline-padded inline-block">{title}</span>
                </h2>
              )}
              <br />
              <p
                className={cn(
                  "leading-relaxed text-base font-bold pt-2",
                  stackDescription ? "w-full mt-2" : "sm:w-3/5 sm:pl-10 pl-0", // Full width if stacked
                  centerTitle && "mx-auto" // Center description
                )}
              >
                {descriptionSlot || description}
              </p>
            </div>

            {/* Content Area */}
            <div
              className={cn()
              // 'px-4 mx-auto max-w-screen-xl gap-8 lg:gap-16'
              // 'mb-5'
              }
            >
              {childrenWithRef}
            </div>

            {/*{bottomDivider && (<Divider/>)}*/}
          </div>
        </div>
      </section>
    );
  }
);

PageSection.displayName = "PageSection";

export default PageSection;
